import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../colorVars';
import { updatePlugins } from '../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/updatePlugins';
import { useWebsocketStore } from '../../../../../../../../stores/websocketStore/websocketProvider';
import { CancelOrderButton } from '../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import IncrementButton from '../../../../../../../atomic/buttons/incrementButton/IncrementButton';
import { MultiSwitch } from '../../../../../../../atomic/buttons/multiSwitch/MultiSwitch';
import CheckBox from '../../../../../../../atomic/formElements/checkBox/CheckBox';
import { EditableTableField } from '../../../../../../../atomic/inputs/editableTableField/EditableTableField';
import { Body3 } from '../../../../../../../atomic/text/Text';
import { IndividualPanelHeader } from '../IndividualPanelHeader';
import style from '../IndividualTacticStyle.module.css';

export const EmaIntersectsFirstCandleTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const periods =
      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']?.periodLengths ?? null;
    const [newPriceInput, setNewPriceInput] = useState(0);

    const updateTactic = (field, newValue) => {
      updatePlugins(
        'setupFilter',
        { [field]: newValue },
        'EMA_INTERSECTS_FIRST_CANDLE',
        tradePlanId,
        null
      );
    };
    if (!tradePlan?.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']) return null;
    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            <tbody>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candle Barrier
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <MultiSwitch
                    labels={['High', 'Low']}
                    values={['HIGH', 'LOW']}
                    controlledValue={
                      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                        .candleBarrier
                    }
                    setValue={(value) => updateTactic('candleBarrier', value)}
                    margin="5px 0 0 0"
                  />
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Direction
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <MultiSwitch
                    labels={['Above', 'Below']}
                    values={['ABOVE', 'BELOW']}
                    controlledValue={
                      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                        .direction
                    }
                    setValue={(value) => updateTactic('direction', value)}
                    margin="5px 0 0 0"
                  />
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candle Duration
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableField
                    color={colors.lightGrey}
                    bodySize={3}
                    backgroundColor="inherit"
                    initialValue={
                      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                        .firstCandleCandleDuration
                    }
                    updateValue={(newValue) => {
                      if (
                        newValue !== 0 &&
                        newValue !==
                          tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                            .firstCandleCandleDuration
                      )
                        updateTactic('firstCandleCandleDuration', newValue);
                    }}
                    inputType="number"
                  />
                </td>

                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    EMA Candle Duration
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableField
                    color={colors.lightGrey}
                    bodySize={3}
                    backgroundColor="inherit"
                    initialValue={
                      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                        .emaCandleDuration
                    }
                    updateValue={(newValue) => {
                      if (
                        newValue !== 0 &&
                        newValue !==
                          tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                            .emaCandleDuration
                      )
                        updateTactic('emaCandleDuration', newValue);
                    }}
                    inputType="number"
                  />
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Associate w/EMA Setup
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <CheckBox
                    fontSize="10px"
                    margin="5px 0 0 0"
                    checked={
                      tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                        .associatePerEmaSetup
                    }
                    onChange={(newValue) => {
                      if (
                        newValue !== 0 &&
                        newValue !==
                          tradePlan.setupFilters['EMA_INTERSECTS_FIRST_CANDLE']
                            .associatePerEmaSetup
                      )
                        updateTactic('associatePerEmaSetup', newValue);
                    }}
                  />
                </td>
                <td></td>
                <td></td>
              </tr>
              {periods.map((price, index) => {
                return (
                  <tr key={price}>
                    <td className={style.labelTd}>
                      <Body3 fontWeight={700} color={colors.lightGrey}>
                        {`Period ${index + 1}`}
                      </Body3>
                    </td>
                    <td className={style.inputTd}>
                      <EditableTableField
                        inputType="number"
                        bodySize={3}
                        initialValue={price}
                        updateValue={(newValue) => {
                          const periodsCopy = [...periods];
                          periodsCopy[index] = newValue;
                          updateTactic('periodLengths', periodsCopy);
                        }}
                        color={colors.lightGrey}
                        backgroundColor="inherit"
                      />
                      <CancelOrderButton
                        width={15}
                        clickHandler={() => {
                          const filteredArr = [...periods].filter(
                            (item, i) => i !== index
                          );
                          updateTactic('periodLengths', filteredArr);
                        }}
                      />
                    </td>
                    <td className={style.labelTd}></td>
                    <td></td>
                  </tr>
                );
              })}
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Add Period
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableField
                    inputType="number"
                    bodySize={3}
                    initialValue={newPriceInput}
                    updateValue={(newValue) => setNewPriceInput(newValue)}
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                  />
                  <IncrementButton
                    color={colors.white}
                    backgroundColor={colors.blue}
                    width={10}
                    stroke={1}
                    handleClick={() => {
                      const periodsCopy = [...periods];
                      periodsCopy.push(newPriceInput);
                      updateTactic('periodLengths', periodsCopy);
                    }}
                    buttonWidth={15}
                  />
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>
    );
  }
);

EmaIntersectsFirstCandleTacticPanel.tabsRole = 'TabPanel';
